import { DatePicker } from "@bjarkehs/react-nice-dates";
import { faPenToSquare, faPlus, faSpinner, faStop } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, Badge, Button, Card, Modal, Select, Stack, Table, Textarea, TextInput } from "@mantine/core";
import { enAU } from "date-fns/locale";
import { DateTime } from "luxon";
import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import { bindActionCreators } from "redux";
import AssociationsApi from "../../../data/api/associationsApi";
import { IState } from "../../../data/reducers/state";
import { IAssociation, IAssociationResource } from "../../../types/association/Association";
import { Role } from "../../../types/persons/Role";
import { AdminEmptyState } from "../common";

interface AssociationResourcesManagerProps {
  selectedAssociation: IAssociation | null;
}

interface ResourceFormData {
  title: string;
  body: string;
  role: string;
  activeFrom: string;
}

const initialFormData: ResourceFormData = {
  title: "",
  body: "",
  role: "",
  activeFrom: new Date().toISOString().split('T')[0],
};

const AssociationResourcesManager: React.FC<AssociationResourcesManagerProps> = ({
  selectedAssociation,
}) => {
  const [resources, setResources] = useState<IAssociationResource[]>([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingResource, setEditingResource] = useState<IAssociationResource | null>(null);
  const [formData, setFormData] = useState<ResourceFormData>(initialFormData);
  const [previewMode, setPreviewMode] = useState(false);
  const [resourceFilter, setResourceFilter] = useState<"all" | "active" | "inactive">("active");
  const todayStr = new Date().toISOString().split('T')[0];

  useEffect(() => {
    if (selectedAssociation) {
      loadResources();
    }
  }, [selectedAssociation]);

  const loadResources = async () => {
    if (!selectedAssociation) return;

    setLoading(true);
    try {
      const data = await AssociationsApi.getAssociationResources(selectedAssociation._id as string);
      setResources(data);
    } catch (error) {
      toast.error("Failed to load resources");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAssociation) return;

    if (!formData.title.trim() || !formData.body.trim()) {
      toast.error("Title and body are required");
      return;
    }

    setLoading(true);
    try {
      const effectiveDateStr = formData.activeFrom && formData.activeFrom < todayStr
        ? todayStr
        : (formData.activeFrom || todayStr);
      const resourceData = {
        title: formData.title.trim(),
        body: formData.body.trim(),
        role: formData.role || undefined,
        activeFrom: new Date(effectiveDateStr),
      };

      if (editingResource) {
        await AssociationsApi.updateAssociationResource(
          selectedAssociation._id as string,
          editingResource._id as string,
          resourceData
        );
        toast.success("Resource updated successfully!");
      } else {
        await AssociationsApi.createAssociationResource(
          selectedAssociation._id as string,
          resourceData
        );
        toast.success("Resource created successfully!");
      }

      await loadResources();
      closeModal();
    } catch (error) {
      toast.error(editingResource ? "Failed to update resource" : "Failed to create resource");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleEndDate = async (resource: IAssociationResource) => {
    if (!selectedAssociation) return;
    if (!confirm(`Are you sure you want to deactivate "${resource.title}"?`)) return;

    setLoading(true);
    try {
      await AssociationsApi.endDateAssociationResource(
        selectedAssociation._id as string,
        resource._id as string
      );
      toast.success("Resource deactivated successfully!");
      await loadResources();
    } catch (error) {
      toast.error("Failed to deactivate resource");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingResource(null);
    setFormData({ ...initialFormData, activeFrom: todayStr });
    setPreviewMode(false);
    setShowModal(true);
  };

  const openEditModal = (resource: IAssociationResource) => {
    setEditingResource(resource);
    const resourceDateStr = resource.activeFrom
      ? new Date(resource.activeFrom).toISOString().split('T')[0]
      : "";
    const clampedActiveFrom = resourceDateStr && resourceDateStr < todayStr
      ? todayStr
      : (resourceDateStr || todayStr);
    setFormData({
      title: resource.title,
      body: resource.body,
      role: resource.role ? String(resource.role) : "",
      activeFrom: clampedActiveFrom,
    });
    setPreviewMode(false);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingResource(null);
    setFormData(initialFormData);
    setPreviewMode(false);
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString();
  };

  const isActive = (resource: IAssociationResource): boolean => {
    const now = new Date();
    const activeFrom = new Date(resource.activeFrom);
    const activeTo = resource.activeTo ? new Date(resource.activeTo) : null;
    return activeFrom <= now && (!activeTo || activeTo > now);
  };

  if (!selectedAssociation) {
    return (
      <AdminEmptyState
        icon="fa fa-hand-pointer"
        title="Select an association"
        description="Choose an association to manage resources."
      />
    );
  }

  return (
    <div className="association-resources-manager">
      <Card>
        <Card.Section withBorder p="md" className="d-flex justify-content-between align-items-center">
          <h4 className="mb-0">Association Resources</h4>
          <Button color="blue" onClick={openCreateModal} disabled={loading}>
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            New Resource
          </Button>
        </Card.Section>
        <div style={{ padding: "1rem" }}>
          {loading && !showModal ? (
            <div className="text-center py-4">
              <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
              Loading resources...
            </div>
          ) : resources.length === 0 ? (
            <AdminEmptyState
              icon="fa fa-book"
              title="No resources yet"
              description="Create your first resource for this association."
              actionButton={{
                text: "Create First Resource",
                onClick: openCreateModal,
                color: "primary",
                icon: "fa fa-plus",
              }}
            />
          ) : (
            <>
              {/* Filter controls */}
              <div className="d-flex justify-content-end mb-3">
                <Button.Group>
                  <Button
                    color={resourceFilter === "all" ? "blue" : "gray"}
                    variant={resourceFilter === "all" ? "filled" : "outline"}
                    onClick={() => setResourceFilter("all")}
                  >
                    All
                  </Button>
                  <Button
                    color={resourceFilter === "active" ? "blue" : "gray"}
                    variant={resourceFilter === "active" ? "filled" : "outline"}
                    onClick={() => setResourceFilter("active")}
                  >
                    Active
                  </Button>
                  <Button
                    color={resourceFilter === "inactive" ? "blue" : "gray"}
                    variant={resourceFilter === "inactive" ? "filled" : "outline"}
                    onClick={() => setResourceFilter("inactive")}
                  >
                    Inactive
                  </Button>
                </Button.Group>
              </div>

              {/* Derive filtered + sorted resources: active first, then by most recent start date */}
              {(() => {
                const filtered = resources.filter((r) => {
                  const active = isActive(r);
                  if (resourceFilter === "active") return active;
                  if (resourceFilter === "inactive") return !active;
                  return true;
                });
                const visible = filtered.sort((a, b) => {
                  const aActive = isActive(a) ? 1 : 0;
                  const bActive = isActive(b) ? 1 : 0;
                  if (bActive - aActive !== 0) return bActive - aActive; // active first
                  const aFrom = new Date(a.activeFrom).getTime();
                  const bFrom = new Date(b.activeFrom).getTime();
                  return bFrom - aFrom; // most recent first
                });

                if (visible.length === 0) {
                  return (
                    <Alert color="light" className="text-center">
                      {resourceFilter === "active" && "No active resources."}
                      {resourceFilter === "inactive" && "No inactive resources."}
                      {resourceFilter === "all" && "No resources to display."}
                    </Alert>
                  );
                }

                return (
                  <Table highlightOnHover>
                    <colgroup>
                      <col />
                      <col style={{ width: "1%" }} />
                      <col style={{ width: "1%" }} />
                      <col style={{ width: "1%" }} />
                      <col style={{ width: "1%" }} />
                      <col style={{ width: "1%" }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th>Title & Content</th>
                        <th style={{ whiteSpace: "nowrap", minWidth: "80px" }}>Role</th>
                        <th style={{ whiteSpace: "nowrap", minWidth: "80px" }}>Status</th>
                        <th style={{ whiteSpace: "nowrap", minWidth: "80px" }}>Active From</th>
                        <th style={{ whiteSpace: "nowrap", minWidth: "80px" }}>Active To</th>
                        <th style={{ whiteSpace: "nowrap" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visible.map((resource) => (
                        <tr key={resource._id as string}>
                          <td className="text-break text-wrap align-middle">
                            <strong>{resource.title}</strong>
                            <br />
                            <small className="text-muted">
                              {resource.body.length > 100
                                ? `${resource.body.substring(0, 100)}...`
                                : resource.body}
                            </small>
                          </td>
                          <td className="align-middle" style={{ whiteSpace: "nowrap" }}>
                            {resource.role ? (
                              <Badge color="gray">{Role.toString(resource.role as any)}</Badge>
                            ) : (
                              <Badge color="blue">All Roles</Badge>
                            )}
                          </td>
                          <td className="align-middle" style={{ whiteSpace: "nowrap" }}>
                            <Badge color={isActive(resource) ? "success" : "secondary"}>
                              {isActive(resource) ? "Active" : "Inactive"}
                            </Badge>
                          </td>
                          <td className="align-middle" style={{ whiteSpace: "nowrap" }}>{formatDate(resource.activeFrom)}</td>
                          <td className="align-middle" style={{ whiteSpace: "nowrap" }}>
                            {resource.activeTo ? formatDate(resource.activeTo) : "—"}
                          </td>
                          <td className="align-middle" style={{ whiteSpace: "nowrap" }}>
                            <div className="btn-group btn-group-sm" role="group">
                              <Button
                                color="blue"
                                variant="outline"

                                onClick={() => openEditModal(resource)}
                                title="Edit (creates new version)"
                              >
                                <FontAwesomeIcon icon={faPenToSquare} />
                              </Button>
                              {isActive(resource) && (
                                <Button
                                  color="orange"
                                  variant="outline"

                                  onClick={() => handleEndDate(resource)}
                                  title="Deactivate"
                                >
                                  <FontAwesomeIcon icon={faStop} />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                );
              })()}
            </>
          )}
        </div>
      </Card>

      {/* Create/Edit Modal */}
      <Modal opened={showModal} onClose={closeModal} size="lg" title={
        <div>
          {editingResource ? "Edit Resource" : "Create New Resource"}
          {editingResource && (
            <small className="d-block text-muted mt-1">
              Note: Editing creates a new version and deactivates the current one.
            </small>
          )}
        </div>
      }>
        <Stack gap="md" component="form" onSubmit={handleSubmit}>
          {!previewMode ? (
            <>
              <Stack gap="sm">
                <label htmlFor="title" className="fw-500">Title *</label>
                <TextInput
                  type="text"
                  name="title"
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Resource title"
                  required
                />
              </Stack>

              <Stack gap="sm">
                <label htmlFor="role" className="fw-500">Visible To Role</label>
                <Select
                  name="role"
                  id="role"
                  value={formData.role}
                  onChange={(value) => setFormData({ ...formData, role: value })}
                  data={[
                    { value: "", label: "All Roles" },
                    { value: Role.referee.toString(), label: "Referees Only" },
                    { value: Role.supervisor.toString(), label: "Supervisors Only" },
                    { value: Role.administrative.toString(), label: "Administrators Only" }
                  ]}
                />
                <small className="form-text text-muted">
                  Leave empty to show to all roles
                </small>
              </Stack>

              <Stack gap="sm">
                <label htmlFor="activeFrom" className="fw-500">Active From (YYYY-MM-DD)</label>
                <div className="nice-dates-compact">
                  <DatePicker
                    date={formData.activeFrom ? DateTime.fromISO(formData.activeFrom).toJSDate() : null}
                    onDateChange={(date) => setFormData((prev) => ({
                      ...prev,
                      activeFrom: date ? DateTime.fromJSDate(date).toISODate() : ""
                    }))}
                    locale={enAU}
                    minimumDate={DateTime.fromISO(todayStr).toJSDate()}
                    format="yyyy-MM-dd"
                  >
                    {({ inputProps, focused }) => (
                      <TextInput
                        {...inputProps}
                        type="text"
                        name="activeFrom"
                        id="activeFrom"
                        placeholder="YYYY-MM-DD"
                        className={`input${focused ? " -focused" : ""}`}
                      />
                    )}
                  </DatePicker>
                </div>
              </Stack>

              <Stack gap="sm">
                <label htmlFor="body" className="fw-500">Content * (Markdown supported)</label>
                <Textarea
                  name="body"
                  id="body"
                  value={formData.body}
                  onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                  placeholder="Resource content in markdown format..."
                  rows={10}
                  required
                />
                <small className="form-text text-muted">
                  You can use markdown formatting (bold, italic, lists, links, etc.)
                </small>
              </Stack>
            </>
          ) : (
            <div className="preview-content">
              <h5>{formData.title}</h5>
              <hr />
              <ReactMarkdown>{formData.body}</ReactMarkdown>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
            <Button
              type="button"
              color="blue"
              onClick={() => setPreviewMode(!previewMode)}
              disabled={!formData.body.trim()}
            >
              {previewMode ? "Edit" : "Preview"}
            </Button>
            <Button type="button" color="gray" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              type="submit"
              color="blue"
              disabled={loading || !formData.title.trim() || !formData.body.trim()}
            >
              {loading && <FontAwesomeIcon icon={faSpinner} spin className="me-2" />}
              {editingResource ? "Update Resource" : "Create Resource"}
            </Button>
          </div>
        </Stack>
      </Modal>
    </div>
  );
};

function mapStateToProps(_state: IState) {
  return {};
}

function mapDispatchToProps(dispatch: any) {
  return {
    actions: bindActionCreators({}, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(AssociationResourcesManager);
